import { WithSelect } from '@components/design';
import { noop } from 'lodash';
import { createToUpdateFilter, isAllowSearchBox, isEmptyOrNoOptions } from '@utils/FilterUtils';
import { FilterKeys, FilterKeyTitleAndExclusion, Filters, Option } from '@utils/pageConfig/Types';
import { multiCountryDisabledFilters } from '@utils/multiCountryUtils/MultiCountryUtils';
import React, { useEffect } from 'react';
import { ValueListType, ValueType } from '@ecosystem/newron-design-system/build/global-types/types';
import { ExclusionProps, WithMultiSelectToggle } from '@components/design/WithMultiSelectToggle';

interface FilterSelectorProps {
  selectFilter: FilterKeyTitleAndExclusion;
  placeholder: string;
  options: Option[];
  value: Option[];
  isMulti?: boolean;
  isDisabledMultiSelect?: boolean;
  isVirtualList?: boolean;
  disableSelectorPredicate?: (options: Option[]) => boolean;
  onChangeFunc?: (toUpdate: Filters) => void;
  exclusion?: ExclusionProps;
}

const FilterSelector: React.FC<FilterSelectorProps> = ({
  selectFilter,
  placeholder,
  options,
  value,
  isMulti = true,
  disableSelectorPredicate,
  isDisabledMultiSelect,
  isVirtualList,
  onChangeFunc = noop,
  exclusion,
}) => {
  const filterItemTestId =
    selectFilter.filterKey === FilterKeys.ADDFEAT
      ? `filter-item-${selectFilter.filterKey}-${selectFilter.filterTitle}`
      : `filter-item-${selectFilter.filterKey}`;

  const isMultiCountry = isMulti && selectFilter.filterKey === FilterKeys.COUNTRY;

  const [isExclusionActive, setIsExclusionActive] = React.useState<boolean>(exclusion?.isActive === true);

  useEffect(() => {
    setIsExclusionActive(exclusion?.isActive === true);
  }, [exclusion?.isActive]);

  const shouldShowSelectAll = () => {
    return [FilterKeys.COUNTRY, FilterKeys.PRODUCT_GROUP].includes(selectFilter.filterKey);
  };

  const shouldShowUnselectAll = () => {
    return [FilterKeys.COUNTRY, FilterKeys.PRODUCT_GROUP, FilterKeys.BRAND].includes(selectFilter.filterKey);
  };

  const handleIsDisabledMultiSelect = (ops: Option[]) => {
    const isDisabledFilters = multiCountryDisabledFilters.includes(selectFilter.filterKey);
    return (isDisabledMultiSelect && isDisabledFilters) || isEmptyOrNoOptions(ops);
  };

  const onSelectAll = () => {
    const selectedOptions = options;
    if (selectedOptions.length > 0) {
      const toUpdate = createToUpdateFilter(selectFilter, selectedOptions, false);
      onChangeFunc?.(toUpdate);
    }
  };

  const onUnselectAll = () => {
    const multiCountryHasValue = isMultiCountry && value.length;
    const values = multiCountryHasValue ? [value[0]] : [];
    onChangeFunc?.(createToUpdateFilter(selectFilter, values, false));
  };

  const getFilterClassName = (filterTitle: string) => {
    const baseClassName = filterTitle.toLowerCase();
    const filterType = isMulti ? 'multi' : 'single';
    return `${filterType}-${baseClassName}-filter`;
  };

  const getOptions = () => {
    const isDisabled = disableSelectorPredicate ? disableSelectorPredicate(options) : false;

    if (isDisabled || handleIsDisabledMultiSelect(options)) {
      return [];
    }

    if (isMultiCountry) {
      return options?.map((option) =>
        value.length === 1 && value.some((v) => option.value === v.value)
          ? {
              ...option,
              isDisabled: true,
            }
          : option,
      );
    }

    return options;
  };

  const getSingleValue = () => {
    if (selectFilter.filterKey === FilterKeys.PRODUCT_GROUP) {
      const shouldPgExist = options.find((option) => option.value === value[0]?.value)?.value;
      if (!shouldPgExist) {
        const toUpdate: Filters = createToUpdateFilter(selectFilter, [options[0]], false);
        onChangeFunc?.(toUpdate);
      }
    }
    return value[0]?.value;
  };

  const getMultiValue = (selectedValue: Option[]): ValueListType => {
    const optionNotExist = selectedValue.filter((v) => !options.some((option) => option.value === v.value));
    if (optionNotExist.length > 0) {
      const updatedValue = selectedValue.filter((v) => options.some((option) => option.value === v.value));
      const toUpdate: Filters = createToUpdateFilter(selectFilter, updatedValue, false, isExclusionActive);
      onChangeFunc?.(toUpdate);
      return updatedValue.map((v) => v.value);
    }
    return selectedValue.map((v) => v.value);
  };

  const onExclusionChange = (
    checked: boolean,
    event: React.SyntheticEvent<MouseEvent | KeyboardEvent> | MouseEvent,
    id: string,
  ): void => {
    const next = !isExclusionActive;
    setIsExclusionActive(next);

    // When exclusion mode changes, we need to unselect all options
    const multiCountryHasValue = isMultiCountry && value.length;
    const values = multiCountryHasValue ? [value[0]] : [];
    onChangeFunc?.(createToUpdateFilter(selectFilter, values, false, next));
  };

  return (
    <div className={`${getFilterClassName(selectFilter.filterTitle)} mb-5 relative`} data-testid={filterItemTestId}>
      {isMulti ? (
        <WithMultiSelectToggle
          exclusion={exclusion}
          toggleSwitchProps={{ isChecked: isExclusionActive, onChange: onExclusionChange }}
          multiSelectProps={{
            id: `filter-item-${selectFilter.filterKey}`,
            name: `filter-item-${selectFilter.filterKey}`,
            label: `${selectFilter.filterTitle}`,
            combobox: isAllowSearchBox(options),
            placeholder,
            isVirtualizedList: isVirtualList && getOptions()?.length > 100,
            ...(selectFilter.filterKey === FilterKeys.COUNTRY && {
              key: `${getOptions()?.map((v) => v.isDisabled)}`,
            }),
            options: getOptions(),
            values: getMultiValue(value),
            disabled: disableSelectorPredicate
              ? disableSelectorPredicate(options)
              : handleIsDisabledMultiSelect(options),
            ...(shouldShowSelectAll() && { isPrimaryLinkButtonEnabled: true }),
            ...(shouldShowUnselectAll() && { isSecondaryLinkButtonEnabled: true }),
            onPrimaryLinkButtonClick: onSelectAll,
            onSecondaryLinkButtonClick: onUnselectAll,
            onChange: (elem: string[], _value: ValueType) => {
              const selectedOptions = options.filter((o) => elem.includes(o.value)) ?? [];
              onChangeFunc?.(createToUpdateFilter(selectFilter, selectedOptions, false, isExclusionActive));
            },
          }}
        />
      ) : (
        <WithSelect
          id={`filter-item-${selectFilter.filterKey}`}
          name={`filter-item-${selectFilter.filterKey}`}
          placeholder={placeholder}
          key={`${options}-${value}`} //NOSONAR typescript:S6551
          options={options}
          value={getSingleValue()}
          label={selectFilter.filterTitle}
          disabled={disableSelectorPredicate ? disableSelectorPredicate(options) : handleIsDisabledMultiSelect(options)}
          onChange={(elem: unknown) => {
            const selectedOption = options.find((o) => o.value === elem);
            if (selectedOption?.value && selectedOption.value !== value[0]?.value) {
              onChangeFunc(createToUpdateFilter(selectFilter, [selectedOption], false, isExclusionActive));
            }
          }}
          combobox={isAllowSearchBox(options)}
        />
      )}
    </div>
  );
};

export default FilterSelector;
